import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, ReferenceLine, Cell } from 'recharts';
import { fetchPpbSegments } from '../services/api';

// Reports > PPB Segments.
//
// Greg asked for an artifact of the analysis he saw in chat (profit per box
// across a week, segmented three ways). Same shipping_report_cache source as
// Daily PPB, pulled over a range and grouped by order-size band, shipping
// method and state, with the losing orders listed under the charts.
//
// Everything a number shows comes from the server (lib/ppb-segments). Each
// chart renders what it was handed and derives nothing — same rule as DailyPpb.

const GOOD = '#1e7e34';
const BAD = '#c0392b';
const WARN = '#b8860b';
const NAVY = '#00395C';
const ORANGE = '#DC582A';

const usd = (v) => {
  const n = Number(v || 0);
  return `${n < 0 ? '-' : ''}$${Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const pctTxt = (v) => (v === null || v === undefined ? '—' : `${Number(v).toFixed(1)}%`);
const signColor = (v) => (Number(v) < 0 ? BAD : GOOD);
const barColor = (profit) => (profit < 0 ? BAD : profit < 20 ? WARN : GOOD);

function etYesterday() {
  const et = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }));
  et.setDate(et.getDate() - 1);
  return et;
}
function isoFor(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function usFor(d) {
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
}
function parseIso(s) {
  const [y, m, d] = String(s || '').split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}
function Tile({ label, value, color, note }) {
  return (
    <div className="card" style={{ flex: 1, minWidth: 160 }}>
      <div className="card-body" style={{ padding: '14px 16px' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '.4px' }}>{label}</div>
        <div style={{ fontSize: 24, fontWeight: 700, color: color || NAVY, marginTop: 4 }}>{value}</div>
        {note ? <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 3 }}>{note}</div> : null}
      </div>
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <div className="card" style={{ marginBottom: 18 }}>
      <div className="card-header">
        <div>
          <div style={{ fontWeight: 700, color: NAVY }}>{title}</div>
          {subtitle ? <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 2, fontWeight: 400 }}>{subtitle}</div> : null}
        </div>
      </div>
      <div className="card-body">{children}</div>
    </div>
  );
}

// Shared tooltip body for the three segment charts — one place that decides
// how to describe a row, so the charts cannot say different things about the
// same number.
function SegTooltip({ active, payload, labelKey }) {
  if (!active || !payload || !payload.length) return null;
  const row = payload[0].payload;
  return (
    <div style={{ background: 'white', border: '1px solid #ddd', padding: 10, fontSize: 12, borderRadius: 4, boxShadow: '0 2px 6px rgba(0,0,0,0.08)' }}>
      <div style={{ fontWeight: 700, marginBottom: 6 }}>{row[labelKey]}</div>
      <div>Orders: <strong>{row.orders}</strong>{row.losing > 0 ? <span style={{ color: BAD }}> ({row.losing} losing)</span> : null}</div>
      <div>Avg per box: <strong style={{ color: signColor(row.meanPpb) }}>{usd(row.meanPpb)}</strong></div>
      <div>Margin: <strong>{pctTxt(row.marginPct)}</strong></div>
      <div>Total profit: <strong style={{ color: signColor(row.totalProfit) }}>{usd(row.totalProfit)}</strong></div>
    </div>
  );
}

// Chart used by all three segment views — a bar per row, coloured by margin
// signal, with a count label and the segment label below. Shared so the three
// cannot drift in appearance.
function SegmentBarChart({ data, labelKey, height = 260, yFormatter }) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={{ top: 10, right: 16, bottom: 10, left: 10 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef0f2" />
        <XAxis dataKey={labelKey} tick={{ fontSize: 11, fill: '#555' }} interval={0} angle={data.length > 6 ? -20 : 0} textAnchor={data.length > 6 ? 'end' : 'middle'} height={data.length > 6 ? 60 : 30} />
        <YAxis tick={{ fontSize: 11, fill: '#888' }} tickFormatter={yFormatter || ((v) => `$${v}`)} />
        <Tooltip content={<SegTooltip labelKey={labelKey} />} />
        <ReferenceLine y={0} stroke="#999" />
        <Bar dataKey="meanPpb" name="Avg per box">
          {data.map((row, i) => (
            <Cell key={i} fill={barColor(row.meanPpb)} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

// A per-day trend bar for the range the user picked. Zero days (nothing cached
// yet) are still plotted so a gap in the data is a visible gap, not a silent one.
function PerDayChart({ perDay }) {
  const data = perDay.map((d) => ({
    date: d.date.slice(0, 5),             // MM/DD, enough on a chart axis
    orders: d.orders,
    totalProfit: d.totalProfit,
    meanPpb: d.meanPpb,
    missing: d.missing,
  }));
  return (
    <ResponsiveContainer width="100%" height={200}>
      <BarChart data={data} margin={{ top: 10, right: 16, bottom: 10, left: 10 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef0f2" />
        <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#555' }} />
        <YAxis tick={{ fontSize: 11, fill: '#888' }} tickFormatter={(v) => `$${v}`} />
        <Tooltip
          formatter={(v, name, props) => {
            if (name === 'meanPpb') return [usd(v), 'Avg per box'];
            if (name === 'totalProfit') return [usd(v), 'Total profit'];
            return [v, name];
          }}
          labelFormatter={(label, items) => {
            const row = items && items[0] && items[0].payload;
            if (!row) return label;
            return `${label} — ${row.orders} box(es)${row.missing ? ' (nothing cached)' : ''}`;
          }}
        />
        <ReferenceLine y={0} stroke="#999" />
        <Bar dataKey="meanPpb" name="Avg per box">
          {data.map((row, i) => (
            <Cell key={i} fill={row.missing ? '#ddd' : barColor(row.meanPpb)} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

export default function PpbSegments({ navigateTo }) {
  // Default to the previous 7 shipping days (ending yesterday ET), which is the
  // "last week" Greg asked about in chat.
  const [to, setTo] = useState(() => etYesterday());
  const [from, setFrom] = useState(() => {
    const d = etYesterday();
    d.setDate(d.getDate() - 6);
    return d;
  });
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const fromUs = usFor(from);
  const toUs = usFor(to);

  const load = useCallback(() => {
    let alive = true;
    setLoading(true); setError(null);
    fetchPpbSegments({ from: fromUs, to: toUs })
      .then((r) => { if (alive) setReport(r); })
      .catch((e) => { if (alive) { setError(e.message); setReport(null); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [fromUs, toUs]);

  useEffect(() => load(), [load]);

  const topStates = useMemo(() => {
    if (!report) return [];
    // Chart gets crowded past ~10 states and most are small — show the ten with
    // the most volume. The table below carries the whole list.
    return report.byState.filter((s) => s.state !== '??').slice(0, 10);
  }, [report]);

  // Shift the whole window by whole weeks so Greg can move through "last week",
  // "two weeks ago" without reaching for the date pickers.
  const shiftRange = (days) => {
    setFrom((f) => { const d = new Date(f); d.setDate(d.getDate() + days); return d; });
    setTo((t) => { const d = new Date(t); d.setDate(d.getDate() + days); return d; });
  };
  const presetLastWeek = () => {
    const t = etYesterday(); const f = new Date(t); f.setDate(f.getDate() - 6);
    setFrom(f); setTo(t);
  };
  const presetLast30 = () => {
    const t = etYesterday(); const f = new Date(t); f.setDate(f.getDate() - 29);
    setFrom(f); setTo(t);
  };

  const h = report && report.headline;

  return (
    <div>
      <div className="breadcrumb">
        <a onClick={() => navigateTo && navigateTo('dashboard')}>Dashboard</a>
        <span> / </span><span>Reports</span><span> / </span><span>PPB Segments</span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', margin: '4px 0 16px' }}>
        <div>
          <h1 style={{ margin: 0, color: NAVY, fontSize: 22 }}>Profit per box by segment</h1>
          <div style={{ fontSize: 13, color: 'var(--gray-500)', marginTop: 2 }}>
            Which order sizes, shipping methods and states make the money, and which are the hard ones.
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-ghost" onClick={() => shiftRange(-7)} title="Previous week">◀ Week</button>
          <input
            type="date"
            className="form-input"
            style={{ width: 150 }}
            value={isoFor(from)}
            onChange={(e) => { const d = parseIso(e.target.value); if (d) setFrom(d); }}
          />
          <span style={{ color: 'var(--gray-500)' }}>to</span>
          <input
            type="date"
            className="form-input"
            style={{ width: 150 }}
            value={isoFor(to)}
            onChange={(e) => { const d = parseIso(e.target.value); if (d) setTo(d); }}
          />
          <button className="btn btn-ghost" onClick={() => shiftRange(7)} title="Next week">Week ▶</button>
          <button className="btn btn-outline" onClick={presetLastWeek}>Last 7 days</button>
          <button className="btn btn-outline" onClick={presetLast30}>Last 30 days</button>
        </div>
      </div>

      {error ? (
        <div className="card"><div className="card-body" style={{ color: BAD }}>Couldn't load: {error}</div></div>
      ) : loading && !report ? (
        <div className="card"><div className="card-body" style={{ color: 'var(--gray-500)' }}>Reading the range…</div></div>
      ) : !report ? null : h.orders === 0 ? (
        <div className="card"><div className="card-body" style={{ color: 'var(--gray-500)' }}>
          No boxes with costs entered in that range yet.
          {report.headline.uncosted ? ` ${report.headline.uncosted} order(s) are still waiting on costs.` : ''}
          {report.missingDays.length ? ` No cached data yet for ${report.missingDays.length} day(s).` : ''}
        </div></div>
      ) : (
        <>
          {/* 1 — headline */}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
            <Tile label="Boxes" value={h.orders.toLocaleString()} note={`${h.profitable} profitable, ${h.losing} losing`} />
            <Tile label="Average per box" value={usd(h.meanPpb)} color={signColor(h.meanPpb)} note="costs entered" />
            <Tile label="Total profit" value={usd(h.totalProfit)} color={signColor(h.totalProfit)} note={`on ${usd(h.totalRevenue)}`} />
            <Tile
              label="Margin"
              value={pctTxt(h.marginPct)}
              color={h.marginPct >= 25 ? GOOD : h.marginPct < 15 ? BAD : WARN}
            />
            <Tile
              label="Reships"
              value={h.reships.orders}
              color={h.reships.orders > 0 ? WARN : NAVY}
              note={h.reships.orders ? `${usd(Math.abs(h.reships.cost))} of cost` : 'none'}
            />
          </div>

          {(h.uncosted > 0 || report.missingDays.length > 0) ? (
            <div className="card" style={{ marginBottom: 18, borderLeft: `4px solid ${WARN}` }}>
              <div className="card-body" style={{ padding: '12px 16px', fontSize: 13, color: 'var(--gray-700)' }}>
                {h.uncosted > 0 ? (
                  <div>⚠️ {h.uncosted} order(s) in this range are still waiting on costs. They are held OUT of the figures above so a half-costed day cannot read as a strong one. Matt or Carman entering costs will move the numbers.</div>
                ) : null}
                {report.missingDays.length > 0 ? (
                  <div style={{ marginTop: h.uncosted > 0 ? 6 : 0 }}>⚠️ {report.missingDays.length} day(s) in this range have no cached shipping report yet: {report.missingDays.join(', ')}. The current day is normally cached only after it closes.</div>
                ) : null}
              </div>
            </div>
          ) : null}

          {/* 2 — order size, Greg's "biggest driver" */}
          <Section
            title="By order size"
            subtitle="Average profit on each box in that size band. Order size is the single biggest driver of margin."
          >
            <SegmentBarChart data={report.bySize} labelKey="band" height={240} />
            <table className="product-table" style={{ marginTop: 10 }}>
              <thead>
                <tr>
                  <th>Band</th>
                  <th style={{ textAlign: 'right' }}>Orders</th>
                  <th style={{ textAlign: 'right' }}>Avg per box</th>
                  <th style={{ textAlign: 'right' }}>Margin</th>
                  <th style={{ textAlign: 'right' }}>Total profit</th>
                  <th style={{ textAlign: 'right' }}>Losing</th>
                </tr>
              </thead>
              <tbody>
                {report.bySize.map((row) => (
                  <tr key={row.band}>
                    <td>{row.band}</td>
                    <td style={{ textAlign: 'right' }}>{row.orders.toLocaleString()}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: row.orders ? signColor(row.meanPpb) : 'var(--gray-400)' }}>{row.orders ? usd(row.meanPpb) : '—'}</td>
                    <td style={{ textAlign: 'right' }}>{pctTxt(row.marginPct)}</td>
                    <td style={{ textAlign: 'right', color: signColor(row.totalProfit) }}>{usd(row.totalProfit)}</td>
                    <td style={{ textAlign: 'right', color: row.losing ? BAD : 'var(--gray-500)' }}>{row.losing}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          {/* 3 — shipping method */}
          <Section
            title="By shipping method"
            subtitle="Air Saver was underperforming last week. Ground services carry the house."
          >
            <SegmentBarChart data={report.byMethod} labelKey="method" height={260} />
            <table className="product-table" style={{ marginTop: 10 }}>
              <thead>
                <tr>
                  <th>Method</th>
                  <th style={{ textAlign: 'right' }}>Orders</th>
                  <th style={{ textAlign: 'right' }}>Avg per box</th>
                  <th style={{ textAlign: 'right' }}>Margin</th>
                  <th style={{ textAlign: 'right' }}>Total profit</th>
                  <th style={{ textAlign: 'right' }}>Losing</th>
                </tr>
              </thead>
              <tbody>
                {report.byMethod.map((row) => (
                  <tr key={row.method}>
                    <td>{row.method}</td>
                    <td style={{ textAlign: 'right' }}>{row.orders.toLocaleString()}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: signColor(row.meanPpb) }}>{usd(row.meanPpb)}</td>
                    <td style={{ textAlign: 'right' }}>{pctTxt(row.marginPct)}</td>
                    <td style={{ textAlign: 'right', color: signColor(row.totalProfit) }}>{usd(row.totalProfit)}</td>
                    <td style={{ textAlign: 'right', color: row.losing ? BAD : 'var(--gray-500)' }}>{row.losing}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          {/* 4 — state */}
          <Section
            title="By state (top 10 by volume)"
            subtitle="Where the money lives and where it leaks. The full state list is in the table below the chart."
          >
            {topStates.length ? <SegmentBarChart data={topStates} labelKey="state" height={240} /> : null}
            <table className="product-table" style={{ marginTop: 10 }}>
              <thead>
                <tr>
                  <th>State</th>
                  <th style={{ textAlign: 'right' }}>Orders</th>
                  <th style={{ textAlign: 'right' }}>Avg per box</th>
                  <th style={{ textAlign: 'right' }}>Margin</th>
                  <th style={{ textAlign: 'right' }}>Total profit</th>
                  <th style={{ textAlign: 'right' }}>Losing</th>
                </tr>
              </thead>
              <tbody>
                {report.byState.map((row) => (
                  <tr key={row.state}>
                    <td>{row.state}</td>
                    <td style={{ textAlign: 'right' }}>{row.orders.toLocaleString()}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: signColor(row.meanPpb) }}>{usd(row.meanPpb)}</td>
                    <td style={{ textAlign: 'right' }}>{pctTxt(row.marginPct)}</td>
                    <td style={{ textAlign: 'right', color: signColor(row.totalProfit) }}>{usd(row.totalProfit)}</td>
                    <td style={{ textAlign: 'right', color: row.losing ? BAD : 'var(--gray-500)' }}>{row.losing}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          {/* 5 — per-day */}
          <Section
            title="Day by day"
            subtitle="Average profit per box each day of the range. A grey bar means nothing cached yet for that day."
          >
            <PerDayChart perDay={report.perDay} />
          </Section>

          {/* 6 — losing orders */}
          <Section
            title={`Losing boxes (${report.worst.length}${h.losing > report.worst.length ? ` of ${h.losing}` : ''})`}
            subtitle="Worst first. Click an order number to open its full page."
          >
            {report.worst.length ? (
              <table className="product-table">
                <thead>
                  <tr>
                    <th>Day</th>
                    <th>Order</th>
                    <th>Customer</th>
                    <th>State</th>
                    <th>Method</th>
                    <th style={{ textAlign: 'right' }}>Revenue</th>
                    <th style={{ textAlign: 'right' }}>Profit</th>
                  </tr>
                </thead>
                <tbody>
                  {report.worst.map((o) => (
                    <tr key={`${o.date}-${o.orderId}`}>
                      <td style={{ color: 'var(--gray-500)' }}>{o.date.slice(0, 5)}</td>
                      <td>
                        <a
                          onClick={() => navigateTo && navigateTo('order-details', o.orderId)}
                          style={{ color: ORANGE, cursor: 'pointer' }}
                        >
                          #{o.bcOrderId || o.orderId}
                        </a>
                      </td>
                      <td style={{ color: 'var(--gray-600)' }}>{o.name}</td>
                      <td>{o.state}</td>
                      <td style={{ color: 'var(--gray-500)', fontSize: 12 }}>{o.shipType}</td>
                      <td style={{ textAlign: 'right' }}>{usd(o.revenue)}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700, color: BAD }}>{usd(o.profit)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div style={{ color: 'var(--gray-500)', fontSize: 13 }}>Every box in this range made money.</div>
            )}
          </Section>

          <div style={{ fontSize: 11, color: 'var(--gray-500)', textAlign: 'center', margin: '14px 0 24px' }}>
            Source: production's own shipping report, cached into Neon. Reships and un-costed boxes are held out of the segment stats and reported separately.
          </div>
        </>
      )}
    </div>
  );
}
